import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from 'axios';
import WhatsAppCard from "./WhatsAppCard";
import { usePoints } from '../context/PointsContext';
import "./LikeAnimation.css";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

console.log("🚀 [System] DetalhesOferta.js carregado v2");

const formatCurrency = (value) => {
  const numericValue = parseFloat(value);
  if (isNaN(numericValue)) {
    return "R$ --";
  }
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(numericValue);
};

const DetalhesOferta = ({ offersData, user, favoriteIds, toggleFavorite, onCategoryChange }) => {
  const { ofertaId } = useParams();
  const { triggerPointsAnimation } = usePoints();
  const [oferta, setOferta] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [relatedOffers, setRelatedOffers] = useState([]);
  const [sidebarOffers, setSidebarOffers] = useState([]);

  const [stores, setStores] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // ID do comentário sendo respondido
  const [replyText, setReplyText] = useState("");

  // Offer likes state
  const [offerLikes, setOfferLikes] = useState(0);
  const [userLikedOffer, setUserLikedOffer] = useState(false);
  const [animatingOfferLike, setAnimatingOfferLike] = useState(false);

  // Price history state
  const [priceHistory, setPriceHistory] = useState([]);

  const isAdmin = false;

  useEffect(() => {
    // Fetch stores
    const fetchStores = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/lojas`);
        setStores(response.data);
      } catch (error) {
        console.error("Erro ao buscar lojas:", error);
      }
    };
    fetchStores();
  }, []);

  useEffect(() => {
    console.log("🔍 [Detalhes] Tentando encontrar ofertaId:", ofertaId, "em offersData de tamanho:", offersData?.length);
    if (offersData && offersData.length > 0) {
      const found = offersData.find((o) => String(o.id) === String(ofertaId));
      console.log("🔍 [Detalhes] Resultado da busca:", found);
      setOferta(found);
      setImageError(false);
      // Related offers (exclude current)
      const others = offersData.filter(o => o.id !== ofertaId);
      setRelatedOffers(others.slice(0, 4));
      setSidebarOffers(others.slice(0, 5)); // Top 5 for sidebar
    }
  }, [offersData, ofertaId]);

  // Fetch comments
  const fetchComments = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/comments/${ofertaId}?t=${Date.now()}`;

      const response = await axios.get(url);
      setComments(response.data);
    } catch (error) {
      console.error("Erro ao buscar comentários:", error);
    }
  };

  // Fetch offer likes
  const fetchOfferLikes = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/offers/${ofertaId}/likes`;

      const response = await axios.get(url);
      setOfferLikes(response.data.likes);
      setUserLikedOffer(response.data.userLiked);
    } catch (error) {
      console.error("Erro ao buscar curtidas da oferta:", error);
    }
  };

  useEffect(() => {
    if (ofertaId) {
      fetchComments();
      fetchOfferLikes();
      
      // Fetch price history
      const fetchHistory = async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/ofertas/${ofertaId}/history`);
          // Formata datas para exibição mais amigável
          const formatted = response.data.map(item => ({
            ...item,
            formattedDate: new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
          }));
          setPriceHistory(formatted);
        } catch (error) {
          console.error("Erro ao buscar histórico de preços:", error);
        }
      };
      fetchHistory();
    }
  }, [ofertaId, user]); // Recarregar se o usuário mudar (login/logout)

  const handleCommentSubmit = async () => {
    if (!newComment.trim()) return;

    setIsSubmittingComment(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/comments`, {
        offerId: ofertaId,
        userId: 'anonymous',
        userName: 'Visitante',
        userPhoto: null,
        comment: newComment
      });

      setNewComment("");
      fetchComments(); // Recarregar para pegar a ordem correta e estrutura
    } catch (error) {
      console.error("Erro ao enviar comentário:", error);
      alert("Erro ao enviar comentário. Tente novamente.");
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const [animatingLikes, setAnimatingLikes] = useState({}); // Track which like buttons are animating

  const handleReaction = async (commentId, type) => {
    // Trigger animation
    if (type === 'like') {
      setAnimatingLikes(prev => ({ ...prev, [commentId]: true }));
      setTimeout(() => {
        setAnimatingLikes(prev => {
          const newState = { ...prev };
          delete newState[commentId];
          return newState;
        });
      }, 400);
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/comments/react`, {
        commentId,
        userId: 'anonymous',
        type
      });
      fetchComments(); // Atualizar contadores
    } catch (error) {
      console.error("Erro ao reagir:", error);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm("Tem certeza que deseja excluir este comentário?")) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/comments/${commentId}`);
      fetchComments();
    } catch (error) {
      console.error("Erro ao deletar:", error);
      alert("Erro ao deletar comentário.");
    }
  };

  const handleReplySubmit = async (parentId) => {
    if (!replyText.trim()) return;

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/comments/${parentId}/reply`, {
        userId: 'anonymous',
        userName: 'Visitante',
        userPhoto: null,
        comment: replyText
      });

      setReplyText("");
      setReplyingTo(null);
      fetchComments();
    } catch (error) {
      console.error("Erro ao responder:", error);
      alert("Erro ao enviar resposta.");
    }
  };

  // Handle offer like
  const handleOfferLike = async (e) => {
    // Capture click position
    const clickX = e.clientX;
    const clickY = e.clientY;

    // Trigger animation
    setAnimatingOfferLike(true);
    setTimeout(() => setAnimatingOfferLike(false), 400);

    try {
      // 1. Registrar Like na Oferta (Contador Visual)
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/offers/${ofertaId}/like`, {
        userId: 'anonymous'
      });

      setOfferLikes(response.data.likes);
      setUserLikedOffer(response.data.userLiked);


    } catch (error) {
      console.error("Erro ao curtir oferta:", error);
      alert("Erro ao curtir oferta. Tente novamente.");
    }
  };

  // Handle Click Reward (Ver na Loja)
  const handleStoreClick = async (e) => {

  };

  // Handle Share Reward (Simulado ao clicar em compartilhar - se houvesse botão)
  // Como não tem botão explícito de share no código original, vou adicionar um simples ao lado do like
  const handleShare = async (e) => {
    const clickX = e.clientX;
    const clickY = e.clientY;
    if (navigator.share) {
      try {
        await navigator.share({
          title: oferta.titulo,
          text: `Confira esta oferta: ${oferta.titulo} por ${formatCurrency(oferta.precoDesconto)}`,
          url: window.location.href,
        });

        // Fallback for points removed

      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback para copy link
      navigator.clipboard.writeText(window.location.href);
      alert("Link copiado para a área de transferência!");
    }
  };

  const getStoreBranding = (storeName) => {
    const name = storeName?.toLowerCase() || "";

    // Try to find in fetched stores
    const foundStore = stores.find(s => s.nome.toLowerCase() === name || name.includes(s.nome.toLowerCase()));

    if (foundStore) {
      return {
        color: foundStore.color,
        textColor: foundStore.textColor,
        logoText: foundStore.nome,
        icon: "fas fa-shopping-bag",
        logoUrl: foundStore.logoUrl
      };
    }

    // Fallback / Default
    return {
      color: "#f5f5f5",
      textColor: "#333333",
      logoText: storeName,
      icon: "fas fa-store",
      logoUrl: null
    };
  };

  const copyCoupon = (couponCode) => {
    navigator.clipboard.writeText(couponCode).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  if (!oferta) {
    return (
      <div className="container" style={{ padding: "50px 0", textAlign: "center" }}>
        <h2 style={{ color: 'var(--text-primary)' }}>Carregando oferta...</h2>
        <Link to="/" style={{ color: "var(--primary-color)", fontWeight: 'bold' }}>
          Voltar para a lista
        </Link>
      </div>
    );
  }

  const isFavorite = favoriteIds.includes(oferta.id);
  const discountPercentage = Math.round(
    ((oferta.precoOriginal - oferta.precoDesconto) / oferta.precoOriginal) * 100
  );
  const storeBrand = getStoreBranding(oferta.loja);

  return (
    <div className="container" style={{ padding: 'var(--spacing-md) 0' }}>

      {/* Breadcrumb & Navigation */}
      <div style={{ 
        marginBottom: 'var(--spacing-md)', 
        padding: '0 var(--spacing-md)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', fontSize: '14px', display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}>
          <i className="fas fa-arrow-left"></i> Voltar para ofertas
        </Link>

        {/* Categoria e Subcategoria */}
        <div className="breadcrumb-container" data-debug-id="breadcrumbs">
          {(oferta.categoria || oferta.category) ? (
            <>
              <span className="breadcrumb-item" onClick={() => onCategoryChange(oferta.categoria || oferta.category)}>
                {oferta.categoria || oferta.category}
              </span>
              {(oferta.subcategoria || oferta.subcategory) && (
                <>
                  <i className="fas fa-chevron-right breadcrumb-separator"></i>
                  <span className="breadcrumb-item" onClick={() => onCategoryChange(oferta.categoria || oferta.category, oferta.subcategoria || oferta.subcategory)}>
                    {oferta.subcategoria || oferta.subcategory}
                  </span>
                </>
              )}
            </>
          ) : (
            <span style={{ color: '#ff0000', fontSize: '10px' }}>DEBUG: Categoria ausente no objeto oferta</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 grid-lg-3" style={{ gap: '20px', alignItems: 'start' }}>

        {/* MAIN CONTENT COLUMN (2/3 width on large screens) */}
        <div style={{ gridColumn: 'span 2' }}>

          {/* Main Offer Card */}
          <div style={{
            backgroundColor: 'var(--white)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            padding: '20px',
            marginBottom: '20px',
            position: 'relative'
          }}>
            <div className="grid grid-cols-1 grid-md-2" style={{ gap: '30px' }}>

              {/* Image Section */}
              <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', padding: '10px' }}>
                <img
                  src={imageError ? "/logo-otyma.png" : ((oferta.imagem || oferta.imagem_url) || "/logo-otyma.png")}
                  alt={oferta.titulo}
                  onError={() => setImageError(true)}
                  style={{ maxWidth: '100%', maxHeight: '350px', objectFit: 'contain' }}
                />
                <button
                  onClick={() => toggleFavorite(oferta.id)}
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    background: 'white',
                    border: '1px solid var(--border-color)',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isFavorite ? 'var(--primary-color)' : 'var(--text-secondary)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    boxShadow: 'var(--shadow-sm)'
                  }}
                >
                  <i className={isFavorite ? "fas fa-heart" : "far fa-heart"}></i>
                </button>

                {/* Offer Like Button */}
                <button
                  onClick={handleOfferLike}
                  className={animatingOfferLike ? 'like-button-animate' : ''}
                  style={{
                    position: 'absolute',
                    top: '45px', // Below favorite
                    right: '0',
                    background: 'white',
                    border: '1px solid var(--border-color)',
                    borderRadius: '20px',
                    padding: '6px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    color: userLikedOffer ? 'var(--accent-color)' : 'var(--text-secondary)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    boxShadow: 'var(--shadow-sm)',
                    fontWeight: userLikedOffer ? 'bold' : 'normal'
                  }}
                  title="Curtir Oferta"
                >
                  <i className={userLikedOffer ? "fas fa-thumbs-up" : "far fa-thumbs-up"}></i>
                  <span style={{ fontSize: '12px' }}>{offerLikes}</span>
                </button>
              </div>

              {/* Info Section */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{
                    backgroundColor: '#ff3b3c',
                    color: 'white',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}>
                    Menor Preço
                  </span>
                </div>

                <h1 style={{ fontSize: '20px', color: 'var(--text-primary)', marginBottom: '15px', lineHeight: '1.4', fontWeight: '600' }}>
                  {oferta.titulo}
                </h1>

                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '36px', fontWeight: '800', color: 'var(--text-primary)', lineHeight: '1' }}>
                    {formatCurrency(oferta.precoDesconto)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <span style={{ textDecoration: 'line-through', color: 'var(--text-secondary)', fontSize: '14px' }}>
                      {formatCurrency(oferta.precoOriginal)}
                    </span>
                    <span style={{ color: '#00a650', fontSize: '13px', fontWeight: 'bold' }}>
                      {discountPercentage}% OFF
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    em até 10x sem juros
                  </div>
                </div>

                {/* Card Otyma Saúde - Telemedicina */}
                <div style={{
                  marginTop: '15px',
                  marginBottom: '15px',
                  padding: '16px',
                  background: 'linear-gradient(135deg, #0d3b66 0%, #0077b6 100%)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  boxShadow: '0 6px 20px rgba(13, 59, 102, 0.15)',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  transition: 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(13, 59, 102, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(13, 59, 102, 0.15)';
                }}
                onClick={() => window.open('https://otymasaude.com.br', '_blank', 'noopener,noreferrer')}
                >
                  {/* Glow decorativo de fundo */}
                  <div style={{
                    position: 'absolute',
                    top: '-50%',
                    right: '-30%',
                    width: '150px',
                    height: '150px',
                    background: 'rgba(0, 180, 216, 0.25)',
                    borderRadius: '50%',
                    filter: 'blur(35px)',
                    pointerEvents: 'none'
                  }} />

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', zIndex: 1 }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      color: '#00b4d8',
                      flexShrink: 0
                    }}>
                      <i className="fas fa-file-prescription"></i>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', letterSpacing: '0.3px', color: '#ffffff' }}>
                        Precisa de receita médica?
                      </h4>
                      <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.4' }}>
                        Renovação de receitas, laudos e consultas online 24h. Emissão imediata!
                      </p>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    backgroundColor: '#ffffff',
                    color: '#0d3b66',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    zIndex: 1,
                    transition: 'background-color 0.2s'
                  }}>
                    Fazer Consulta Online <i className="fas fa-arrow-right" style={{ fontSize: '11px' }}></i>
                  </div>
                </div>

                {/* Store Card - "Official" Look */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '15px',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #eee',
                    overflow: 'hidden'
                  }}>
                    {storeBrand.logoUrl ? (
                      <img src={storeBrand.logoUrl} alt={storeBrand.logoText} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '5px' }} />
                    ) : (
                      <i className={storeBrand.icon} style={{ color: storeBrand.textColor === '#ffffff' ? '#333' : storeBrand.textColor, fontSize: '20px' }}></i>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Vendido e entregue por</div>
                    <div style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '16px' }}>{storeBrand.logoText}</div>
                  </div>
                  <div style={{ marginLeft: 'auto', color: '#00a650', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <i className="fas fa-check-circle"></i> Oficial
                  </div>
                </div>

                {oferta.cupom && (
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{
                      border: '1px dashed var(--primary-color)',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: 'rgba(52, 131, 250, 0.05)'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>CUPOM</span>
                        <span style={{ fontWeight: 'bold', color: 'var(--primary-color)', fontSize: '14px' }}>{oferta.cupom}</span>
                      </div>
                      <button
                        onClick={() => copyCoupon(oferta.cupom)}
                        style={{
                          border: 'none',
                          background: 'none',
                          color: isCopied ? 'var(--accent-color)' : 'var(--primary-color)',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {isCopied ? 'COPIADO' : 'COPIAR'}
                      </button>
                    </div>
                  </div>
                )}

                <a
                  href={oferta.link || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleStoreClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    padding: '14px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    boxShadow: '0 2px 4px rgba(52, 131, 250, 0.2)',
                    marginTop: 'auto',
                    transition: 'background-color 0.2s'
                  }}
                >
                  Ir para a Loja <i className="fas fa-external-link-alt" style={{ marginLeft: '8px' }}></i>
                </a>

                {/* Share Button (New) */}
                <button
                  onClick={handleShare}
                  style={{
                    marginTop: '10px',
                    width: '100%',
                    padding: '10px',
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px'
                  }}
                >
                  <i className="fas fa-share-alt"></i> Compartilhar Oferta
                </button>

              </div>
            </div>
          </div>

          {/* Price History Chart */}
          {priceHistory.length > 1 && (
            <div style={{
              backgroundColor: 'var(--white)',
              borderRadius: 'var(--radius-md)',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '20px'
            }}>
              <h3 style={{ fontSize: '18px', marginBottom: '20px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fas fa-chart-line" style={{ color: 'var(--primary-color)' }}></i> Histórico de Preços
              </h3>
              <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={priceHistory}>
                    <defs>
                      <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--primary-color)" stopOpacity={0.1}/>
                        <stop offset="95%" stopColor="var(--primary-color)" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis 
                      dataKey="formattedDate" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#888' }}
                    />
                    <YAxis 
                      hide={true}
                      domain={['dataMin - 10', 'dataMax + 10']}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Preço']}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="price" 
                      stroke="var(--primary-color)" 
                      strokeWidth={3}
                      fillOpacity={1} 
                      fill="url(#colorPrice)" 
                      animationDuration={1500}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p style={{ fontSize: '12px', color: '#999', marginTop: '10px', textAlign: 'center' }}>
                * O gráfico mostra as variações de preço registradas nos últimos dias.
              </p>
            </div>
          )}

          {/* Comments Section */}
          <div style={{ backgroundColor: 'var(--white)', borderRadius: 'var(--radius-md)', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '20px', color: 'var(--text-primary)' }}>Comentários ({comments.length})</h3>

            {/* Comment Input */}
            <div style={{ display: 'flex', gap: '15px', marginBottom: '30px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                {user && user.photoURL ? (
                  <img src={user.photoURL} alt="User" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: '20px' }}>👤</span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <textarea
                  placeholder={user ? "Escreva um comentário..." : "Faça login para comentar..."}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  disabled={!user || isSubmittingComment}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    minHeight: '80px',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    resize: 'vertical',
                    backgroundColor: user ? 'white' : '#f9f9f9'
                  }}
                ></textarea>
                <div style={{ textAlign: 'right', marginTop: '8px' }}>
                  <button
                    onClick={handleCommentSubmit}
                    disabled={!user || !newComment.trim() || isSubmittingComment}
                    style={{
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      padding: '8px 20px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      opacity: (!user || !newComment.trim() || isSubmittingComment) ? 0.6 : 1
                    }}
                  >
                    {isSubmittingComment ? 'Enviando...' : 'Comentar'}
                  </button>
                </div>
              </div>
            </div>

            {/* Comments List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {comments.length > 0 ? (
                comments.filter(c => !c.parentId).map((comment) => {
                  const replies = comments.filter(r => r.parentId === comment.id);
                  // Ordenar respostas: antigas primeiro
                  replies.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

                  return (
                    <div key={comment.id} style={{ borderBottom: '1px solid #eee', paddingBottom: '20px' }}>
                      {/* Comentário Principal */}
                      <div style={{ display: 'flex', gap: '15px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                          {comment.userPhoto ? (
                            <img src={comment.userPhoto} alt={comment.userName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <span style={{ fontSize: '18px', color: '#666' }}>{comment.userName.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '14px' }}>{comment.userName}</span>
                            <span style={{ fontSize: '12px', color: '#999' }}>
                              {new Date(comment.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.5', margin: '0 0 10px 0' }}>
                            {comment.comment}
                          </p>

                          {/* Ações do Comentário */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            {/* Like */}
                            <button
                              onClick={() => handleReaction(comment.id, 'like')}
                              className={animatingLikes[comment.id] ? 'like-button-animate' : ''}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: comment.userReaction === 'like' ? 'var(--accent-color)' : '#888',
                                display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px'
                              }}
                            >
                              <i className="fas fa-thumbs-up"></i> {comment.likes || 0}
                            </button>

                            {/* Dislike */}
                            <button
                              onClick={() => handleReaction(comment.id, 'dislike')}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: comment.userReaction === 'dislike' ? 'red' : '#888',
                                display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px'
                              }}
                            >
                              <i className="fas fa-thumbs-down"></i>
                              {isAdmin && (comment.dislikes || 0)}
                            </button>

                            {/* Admin Actions */}
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => {
                                    setReplyingTo(replyingTo === comment.id ? null : comment.id);
                                    setReplyText("");
                                  }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', fontSize: '12px', fontWeight: 'bold' }}
                                >
                                  Responder
                                </button>
                                <button
                                  onClick={() => handleDelete(comment.id)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'red', fontSize: '12px' }}
                                >
                                  Excluir
                                </button>
                              </>
                            )}
                          </div>

                          {/* Campo de Resposta (Admin) */}
                          {replyingTo === comment.id && (
                            <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                              <input
                                type="text"
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Escreva uma resposta..."
                                style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                              />
                              <button
                                onClick={() => handleReplySubmit(comment.id)}
                                style={{ backgroundColor: 'var(--primary-color)', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '4px', cursor: 'pointer' }}
                              >
                                Enviar
                              </button>
                            </div>
                          )}

                          {/* Respostas Aninhadas */}
                          {replies.length > 0 && (
                            <div style={{ marginTop: '15px', paddingLeft: '15px', borderLeft: '3px solid #eee' }}>
                              {replies.map(reply => (
                                <div key={reply.id} style={{ marginBottom: '10px', backgroundColor: '#f9f9f9', padding: '10px', borderRadius: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                                    <div style={{ width: '24px', height: '24px', borderRadius: '50%', overflow: 'hidden' }}>
                                      {reply.userPhoto ? (
                                        <img src={reply.userPhoto} alt={reply.userName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                      ) : (
                                        <i className="fas fa-user-circle" style={{ fontSize: '24px', color: '#ccc' }}></i>
                                      )}
                                    </div>
                                    <span style={{ fontWeight: 'bold', fontSize: '13px', color: reply.isAdminReply ? 'var(--primary-color)' : '#333' }}>
                                      {reply.userName} {reply.isAdminReply && <span style={{ fontSize: '10px', backgroundColor: 'var(--primary-color)', color: 'white', padding: '2px 6px', borderRadius: '10px', marginLeft: '5px' }}>ADMIN</span>}
                                    </span>
                                    <span style={{ fontSize: '11px', color: '#999' }}>{new Date(reply.timestamp).toLocaleDateString()}</span>
                                  </div>
                                  <p style={{ fontSize: '13px', margin: 0, color: '#555' }}>{reply.comment}</p>

                                  {/* Admin pode deletar respostas também */}
                                  {isAdmin && (
                                    <div style={{ marginTop: '5px', textAlign: 'right' }}>
                                      <button
                                        onClick={() => handleDelete(reply.id)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'red', fontSize: '11px' }}
                                      >
                                        Excluir
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px 0', fontSize: '14px', fontStyle: 'italic' }}>
                  Seja o primeiro a comentar nesta oferta!
                </div>
              )}
            </div>
          </div>

          {/* Description Section */}
          <div style={{
            backgroundColor: 'var(--white)',
            padding: '20px',
            marginBottom: '20px',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: '18px', marginBottom: '15px', color: 'var(--text-primary)' }}>Detalhes do Produto</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '14px' }}>
              {oferta.descricao}
            </p>
          </div>

          {/* Related Products Carousel */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '15px', color: 'var(--text-primary)', paddingLeft: '10px' }}>Quem viu, viu também</h3>
            <div className="grid grid-cols-2 grid-md-4" style={{ gap: '15px' }}>
              {relatedOffers.map((related) => (
                <Link to={`/oferta/${related.id}`} key={related.id} style={{ textDecoration: 'none' }}>
                  <div style={{ backgroundColor: 'var(--white)', borderRadius: '8px', padding: '12px', border: '1px solid var(--border-color)', height: '100%', transition: 'transform 0.2s' }}>
                    <img src={related.imagem} alt={related.titulo} style={{ width: '100%', height: '120px', objectFit: 'contain', marginBottom: '8px' }} />
                    <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: '600', marginBottom: '4px', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '36px' }}>
                      {related.titulo}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                      {formatCurrency(related.precoDesconto)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

        </div>

        {/* SIDEBAR COLUMN (1/3 width on large screens) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* WhatsApp Group Card */}
          <WhatsAppCard />

          {/* Top Offers Sidebar Widget */}
          <div style={{ backgroundColor: 'var(--white)', borderRadius: 'var(--radius-md)', padding: '15px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '16px', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>🔥 Top Ofertas</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {sidebarOffers.map((offer) => (
                <Link to={`/oferta/${offer.id}`} key={offer.id} style={{ display: 'flex', gap: '10px', textDecoration: 'none', alignItems: 'center' }}>
                  <img src={offer.imagem} alt={offer.titulo} style={{ width: '60px', height: '60px', objectFit: 'contain', border: '1px solid #eee', borderRadius: '4px' }} />
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: '600', marginBottom: '4px', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {offer.titulo}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#00a650' }}>
                      {formatCurrency(offer.precoDesconto)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default DetalhesOferta;
